import { ICommonSettings, IGoogleMcpSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { McpClientService } from "../../../../../services/mcp-client-service";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInfoBox from "../../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigGoogleMcpSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IGoogleMcpSetting;
}

export interface IConfigGoogleMcpSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingGoogleMcpId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigGoogleMcpSettings extends React.PureComponent<
  IConfigGoogleMcpSettingsProps,
  IConfigGoogleMcpSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigGoogleMcpSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigGoogleMcpSettingsProps & IWithSDProps) {
    if (prevProps.editingGoogleMcpId !== this.props.editingGoogleMcpId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    const editingConfig = this.props.commonSettings.googleMcpSettings?.find(
      (x) => x.id === this.props.editingGoogleMcpId
    );
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      if (editingConfig) this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const googleMcpSetting = this.props.commonSettings?.googleMcpSettings;
    if (!googleMcpSetting) return;

    const isSettingAlreadyExist = googleMcpSetting.some(
      (x) => x.name === value && x.id !== this.state.editingConfig?.id
    );

    if (isSettingAlreadyExist) throw new Error("Google Mcp Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IGoogleMcpSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      clientId: data.clientId,
      clientSecret: data.clientSecret,
      searchApiKey: data.searchApiKey,
      searchEngineId: data.searchEngineId,
      emailAddress: data.emailAddress
    };

    if (this.state.editingConfig) {
      obj.id = this.state.editingConfig.id;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleMcpSettings",
        onSuccess: () => {
          McpClientService.CheckGoogleAuth(obj.id)
            .then((response) => {
              if (response.data.authUrlRedirectionRequired) {
                window.open(response.data.redirectUrl, "_blank");
                // The dialog stays for the authorization that just opened in a new tab, so the ✓
                // is all this save gets to say.
                this.saveStatus.succeed();
              } else {
                this.saveStatus.succeed(() => {
                  this.props.closeModal();
                });
              }
            })
            .catch((e) => {
              KMainFunctions.exceptionHandler(e);
              this.saveStatus.fail();
            });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleMcpSettings",
        onSuccess: () => {
          McpClientService.CheckGoogleAuth(obj.id)
            .then((response) => {
              if (response.data.authUrlRedirectionRequired) {
                window.open(response.data.redirectUrl, "_blank");
                this.setState({ editingConfig: obj }, () => {
                  this.formRef.current?.resetFields();
                  this.formRef.current?.setFieldsValue(obj);
                });
                // The dialog stays for the authorization that just opened in a new tab, so the ✓
                // is all this save gets to say.
                this.saveStatus.succeed();
              } else {
                this.saveStatus.succeed(() => {
                  this.props.closeModal();
                });
              }
            })
            .catch((e) => {
              KMainFunctions.exceptionHandler(e);
              this.saveStatus.fail();
            });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.state.editingConfig?.id) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.state.editingConfig?.id || "",
      configKey: "googleMcpSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Google Mcp setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Google Mcp setting"
        secondHeader="Google Mcp setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.state.editingConfig ? this.deleteSetting : undefined}
            deleteTestId="mcpGoogleSettingDeleteButton"
            submitTestId="mcpGoogleSettingCreateButton"
            submitLabel={this.state.editingConfig ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configGoogleMcpForm"
          />
        }
      >
        <Form
          id="configGoogleMcpForm"
          name="googleMcpSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("mcp_googlemcpsetting_description")}>
            <ConfigField label="Name" required tooltipContentKey="mcp_googlemcpsetting_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Google McpName." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Google McpName is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="mcpGoogleSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Client ID" required tooltipContentKey="mcp_googlemcpsetting_clientid">
              <Form.Item name="clientId" rules={[{ required: true, message: "Please enter a Google Mcp Client ID." }]}>
                <KuikaInput size="md" data-testid="mcpGoogleSettingClientIdInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Client Secret" required tooltipContentKey="mcp_googlemcpsetting_clientsecret">
              <Form.Item name="clientSecret" rules={[{ required: true, message: "Please enter a Google Mcp  Id." }]}>
                <KuikaInput size="md" data-testid="mcpGoogleSettingClientSecretInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="E-Mail Address" required tooltipContentKey="mcp_googlemcpsetting_e_mailaddress">
              <Form.Item
                name="emailAddress"
                rules={[{ required: true, message: "Please enter a Google E-Mail Address." }]}
              >
                <KuikaInput size="md" data-testid="mcpGoogleSettingEmailAddressInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Search API Key" required tooltipContentKey="mcp_googlemcpsetting_searchapikey">
              <Form.Item
                name="searchApiKey"
                rules={[{ required: true, message: "Please enter a Google Search API Key." }]}
              >
                <KuikaInput size="md" data-testid="mcpGoogleSettingSearchApiKeyInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Search Engine ID" required tooltipContentKey="mcp_googlemcpsetting_searchengineid">
              <Form.Item
                name="searchEngineId"
                rules={[{ required: true, message: "Please enter a Google Mcp Search Engine Id." }]}
              >
                <KuikaInput size="md" data-testid="mcpGoogleSettingSearchEngineIdInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <KuikaInfoBox
              color="blue"
              leftUnit={
                <div className="kuika-flex--gap-xs">
                  <KuikaSvg name="info-circle" />
                  <KuikaSmallText>
                    Enabled with permission through your Google account. Once the permission step is completed, you will
                    be redirected back to Kuika via the redirect URL.
                  </KuikaSmallText>
                </div>
              }
            ></KuikaInfoBox>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
